import { MAZE_REVEAL_RADIUS, type MazeTileView } from "../../shared/game";
import { SITE_TUNING } from "./siteTuning";

export type MazeBoardRenderTile = MazeTileView & {
    revealed: boolean;
    revealOpacity: number;
    blocked: boolean;
    displayMark: string;
    displayWord: string;
    pressed: boolean;
};

export type MazeCanvasFramePerfSample = {
    ambient: boolean;
    blockedTileCount: number;
    canvasCssHeight: number;
    canvasCssWidth: number;
    canvasPixelHeight: number;
    canvasPixelWidth: number;
    collapseTileCount: number;
    currentTileCount: number;
    devicePixelRatio: number;
    endTileCount: number;
    glowTileCount: number;
    phases: Record<string, number>;
    portalTileCount: number;
    pressedTileCount: number;
    reason: string;
    recordedAt: number;
    startTileCount: number;
    tileCount: number;
    totalMs: number;
    trailTileCount: number;
};

export type MazeCanvasRendererDiagnostics = {
    animatedAccentPixelCount: number;
    canvasPixelCount: number;
    devicePixelRatio: number;
    lastStaticTileBlitCount: number;
    lastStaticTileRasterCount: number;
    staticLayerRebuildCount: number;
    staticLayerPresentCount: number;
    staticTileBlitCount: number;
    staticTileCacheHitCount: number;
    staticTileCacheMissCount: number;
    staticTileRasterCount: number;
    tileBitmapCacheEntryCount: number;
    tileBitmapCachePixelCount: number;
};

type MazeCanvasRendererOptions = {
    onPerfSample?: ((sample: MazeCanvasFramePerfSample) => void) | null;
    perfThresholdMs?: number;
};

type CollapseTileAnimation = {
    durationMs: number;
    offsetX: number;
    offsetY: number;
    startedAt: number;
    tile: MazeBoardRenderTile;
};

type WorldBounds = {
    height: number;
    minX: number;
    minY: number;
    width: number;
};

type TileBitmapCacheEntry = {
    canvas: HTMLCanvasElement;
    pixelCount: number;
    padding: number;
    size: number;
};

const CELL_SIZE = SITE_TUNING.relicRun.board.cellSizePx;
const TILE_SIZE = SITE_TUNING.relicRun.board.tileSizePx;
const TILE_HALF = TILE_SIZE / 2;
const TILE_RADIUS = 22;
const WORLD_PADDING_PX = 148;
const AMBIENT_FRAME_MIN_MS = 66;
const MAX_DEVICE_PIXEL_RATIO = SITE_TUNING.relicRun.board.maxCanvasDevicePixelRatio;
const MAX_CANVAS_BACKING_PIXELS = SITE_TUNING.relicRun.board.maxCanvasBackingPixels;
const MAX_TILE_BITMAP_CACHE_PIXELS = SITE_TUNING.relicRun.board.maxTileBitmapCachePixels;
const TILE_BITMAP_PADDING_PX = 28;
const GOLD = "246, 189, 62";
const TILE_TINT_FILL_MIX = SITE_TUNING.relicRun.theme.canvasTileTintMix;
const TILE_TINT_SHINE_ALPHA = SITE_TUNING.relicRun.theme.canvasTileShineAlpha;
const TILE_TEXT_MIN_FONT_SIZE = 9;
const TILE_TEXT_ELLIPSIS = "...";

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

const mix = (from: number, to: number, amount: number): number => from + (to - from) * amount;

const easeOut = (value: number): number => 1 - Math.pow(1 - clamp(value, 0, 1), 3);

const hashString = (value: string): number => {
    let hash = 2166136261;

    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }

    return hash >>> 0;
};

const unitFromSeed = (seed: number, offset: number): number => {
    const next = Math.sin(seed * 0.0013 + offset * 78.233) * 43758.5453123;
    return next - Math.floor(next);
};

const rgba = (red: number, green: number, blue: number, alpha: number): string =>
    `rgba(${Math.round(red)}, ${Math.round(green)}, ${Math.round(blue)}, ${alpha})`;

const parseTint = (value: string): [number, number, number] => {
    const parts = value
        .trim()
        .split(/\s+/)
        .map((part) => Number.parseFloat(part));

    if (parts.length >= 3 && parts.every((part) => Number.isFinite(part))) {
        return [parts[0] ?? 255, parts[1] ?? 255, parts[2] ?? 255];
    }

    return [255, 255, 255];
};

const sameTint = (left: [number, number, number], right: [number, number, number]): boolean =>
    left[0] === right[0] && left[1] === right[1] && left[2] === right[2];

const buildTileRenderSignature = (tile: MazeBoardRenderTile): string =>
    [
        tile.key,
        tile.x,
        tile.y,
        tile.kind,
        tile.displayWord,
        tile.displayMark,
        tile.revealed ? 1 : 0,
        tile.blocked ? 1 : 0,
        tile.pressed ? 1 : 0,
        tile.isCurrent ? 1 : 0,
        tile.isReachable ? 1 : 0,
        tile.isNextStep ? 1 : 0
    ].join("\u001f");

const buildTilesRenderSignature = (tiles: readonly MazeBoardRenderTile[]): string =>
    tiles.map(tile => `${buildTileRenderSignature(tile)}\u001f${tile.revealOpacity}`).join("\u001e");

const trimCanvasTextToWidth = (
    context: CanvasRenderingContext2D,
    text: string,
    maxWidth: number
): string => {
    if (context.measureText(text).width <= maxWidth) {
        return text;
    }

    let left = 0;
    let right = text.length;
    let best = TILE_TEXT_ELLIPSIS;

    while (left <= right) {
        const midpoint = Math.floor((left + right) / 2);
        const candidate = `${text.slice(0, midpoint).trimEnd()}${TILE_TEXT_ELLIPSIS}`;

        if (context.measureText(candidate).width <= maxWidth) {
            best = candidate;
            left = midpoint + 1;
            continue;
        }

        right = midpoint - 1;
    }

    return best;
};

const roundedRectPath = (
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
) => {
    const safeRadius = Math.min(radius, width / 2, height / 2);

    context.beginPath();
    context.moveTo(x + safeRadius, y);
    context.arcTo(x + width, y, x + width, y + height, safeRadius);
    context.arcTo(x + width, y + height, x, y + height, safeRadius);
    context.arcTo(x, y + height, x, y, safeRadius);
    context.arcTo(x, y, x + width, y, safeRadius);
    context.closePath();
};

type TilePalette = {
    borderColor: string;
    glowColor: string | null;
    gradientBottom: [number, number, number];
    gradientTop: [number, number, number];
    insetColor: string;
    markColor: string;
    shadowAlpha: number;
    wordColor: string;
};

export class MazeCanvasRenderer {
    private readonly canvas: HTMLCanvasElement;
    private readonly context: CanvasRenderingContext2D | null;
    private readonly accentCanvas: HTMLCanvasElement;
    private readonly accentContext: CanvasRenderingContext2D | null;
    private readonly onPerfSample: ((sample: MazeCanvasFramePerfSample) => void) | null;
    private readonly perfThresholdMs: number;
    private readonly staticLayerCanvas: HTMLCanvasElement | null;
    private readonly staticLayerContext: CanvasRenderingContext2D | null;
    private ambientFrameId: number | null = null;
    private bounds: WorldBounds = {
        minX: -WORLD_PADDING_PX,
        minY: -WORLD_PADDING_PX,
        width: WORLD_PADDING_PX * 2,
        height: WORLD_PADDING_PX * 2
    };
    private readonly collapseTiles = new Map<string, CollapseTileAnimation>();
    private devicePixelRatio = 1;
    private lastAmbientFrameAt = 0;
    private lastFramePerfSample: MazeCanvasFramePerfSample | null = null;
    private lastNonAmbientFramePerfSample: MazeCanvasFramePerfSample | null = null;
    private levelTint: [number, number, number] = [255, 255, 255];
    private pendingRenderReasons = new Set<string>();
    private queuedFrameId: number | null = null;
    private staticLayerDirty = true;
    private readonly tileBitmapCache = new Map<string, TileBitmapCacheEntry>();
    private tileBitmapCachePixelCount = 0;
    private staticLayerRebuildCount = 0;
    private staticLayerPresentCount = 0;
    private staticTileBlitCount = 0;
    private staticTileCacheHitCount = 0;
    private staticTileCacheMissCount = 0;
    private staticTileRasterCount = 0;
    private lastStaticTileBlitCount = 0;
    private lastStaticTileRasterCount = 0;
    private tilesRenderSignature = "";
    private tiles: MazeBoardRenderTile[] = [];
    private readonly reducedMotionQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)") ?? null;

    private readonly handleMotionChange = () => {
        if (this.reducedMotionQuery?.matches) {
            this.collapseTiles.clear();
            this.staticLayerDirty = true;
        }
        this.queueRender("motion-change");
        this.syncAmbientLoop();
    };

    constructor(canvas: HTMLCanvasElement, options: MazeCanvasRendererOptions = {}) {
        this.canvas = canvas;
        this.context = canvas.getContext("2d");
        this.accentCanvas = document.createElement("canvas");
        this.accentCanvas.className = canvas.className;
        this.accentCanvas.dataset.mazeAccentLayer = "";
        this.accentCanvas.setAttribute("aria-hidden", "true");
        canvas.after(this.accentCanvas);
        this.accentContext = this.accentCanvas.getContext("2d");
        this.staticLayerCanvas =
            typeof document === "undefined" ? null : document.createElement("canvas");
        this.staticLayerContext = this.staticLayerCanvas?.getContext("2d") ?? null;
        this.onPerfSample = options.onPerfSample ?? null;
        this.perfThresholdMs = options.perfThresholdMs ?? 18;
        this.reducedMotionQuery?.addEventListener("change", this.handleMotionChange);
    }

    destroy() {
        this.accentCanvas.remove();
        this.reducedMotionQuery?.removeEventListener("change", this.handleMotionChange);
        if (this.queuedFrameId !== null) {
            window.cancelAnimationFrame(this.queuedFrameId);
            this.queuedFrameId = null;
        }

        if (this.ambientFrameId !== null) {
            window.cancelAnimationFrame(this.ambientFrameId);
            this.ambientFrameId = null;
        }
    }

    setTiles(tiles: MazeBoardRenderTile[]) {
        const nextTiles = tiles.map((tile) => ({ ...tile }));
        const nextRenderSignature = buildTilesRenderSignature(nextTiles);

        if (nextRenderSignature === this.tilesRenderSignature && this.collapseTiles.size === 0) {
            this.syncAmbientLoop();
            return;
        }

        this.tiles = nextTiles;
        this.tilesRenderSignature = nextRenderSignature;
        this.staticLayerDirty = true;
        this.syncBounds();
        this.queueRender("tile-sync");
        this.syncAmbientLoop();
    }

    refresh() {
        this.syncBounds();
        this.queueRender("refresh");
        this.syncAmbientLoop();
    }

    clearTransientState() {
        if (this.collapseTiles.size === 0) {
            return;
        }

        this.collapseTiles.clear();
        this.staticLayerDirty = true;
        this.queueRender("clear-transient");
        this.syncAmbientLoop();
    }

    startTileCollapse(tile: MazeBoardRenderTile, offsetX: number, offsetY: number, durationMs: number) {
        if (this.reducedMotionQuery?.matches) {
            return;
        }
        this.collapseTiles.set(tile.key, {
            tile: { ...tile, pressed: false },
            offsetX,
            offsetY,
            durationMs,
            startedAt: performance.now()
        });
        this.staticLayerDirty = true;
        this.syncBounds();
        this.queueRender("collapse-start");
        this.syncAmbientLoop();
    }

    getLastFramePerfSample(preferNonAmbient = false): MazeCanvasFramePerfSample | null {
        const sample =
            (preferNonAmbient ? this.lastNonAmbientFramePerfSample : null) ?? this.lastFramePerfSample;

        return sample
            ? {
                  ...sample,
                  phases: { ...sample.phases }
              }
            : null;
    }

    getDiagnostics(): MazeCanvasRendererDiagnostics {
        return {
            animatedAccentPixelCount: this.accentCanvas.width * this.accentCanvas.height,
            canvasPixelCount: this.canvas.width * this.canvas.height,
            devicePixelRatio: this.devicePixelRatio,
            lastStaticTileBlitCount: this.lastStaticTileBlitCount,
            lastStaticTileRasterCount: this.lastStaticTileRasterCount,
            staticLayerRebuildCount: this.staticLayerRebuildCount,
            staticLayerPresentCount: this.staticLayerPresentCount,
            staticTileBlitCount: this.staticTileBlitCount,
            staticTileCacheHitCount: this.staticTileCacheHitCount,
            staticTileCacheMissCount: this.staticTileCacheMissCount,
            staticTileRasterCount: this.staticTileRasterCount,
            tileBitmapCacheEntryCount: this.tileBitmapCache.size,
            tileBitmapCachePixelCount: this.tileBitmapCachePixelCount
        };
    }

    setLevelTint(tint: string) {
        const nextTint = parseTint(tint);

        if (sameTint(this.levelTint, nextTint)) {
            return;
        }

        this.levelTint = nextTint;
        this.clearTileBitmapCache();
        this.staticLayerDirty = true;
        this.queueRender("tint-sync");
    }

    private queueRender(reason: string) {
        this.pendingRenderReasons.add(reason);

        if (this.queuedFrameId !== null) {
            return;
        }

        this.queuedFrameId = window.requestAnimationFrame((time) => {
            this.queuedFrameId = null;
            const reasons = [...this.pendingRenderReasons];
            this.pendingRenderReasons.clear();
            this.render(time, reasons.length > 0 ? reasons.join("+") : reason, false);
        });
    }

    private syncAmbientLoop() {
        const needsAnimation =
            !this.reducedMotionQuery?.matches &&
            (this.collapseTiles.size > 0 ||
                this.tiles.some(
                    (tile) => tile.kind === "start" || tile.kind === "end" || tile.kind === "portal" || tile.isCurrent
                ));

        if (!needsAnimation) {
            if (this.ambientFrameId !== null) {
                window.cancelAnimationFrame(this.ambientFrameId);
                this.ambientFrameId = null;
            }

            return;
        }

        if (this.ambientFrameId !== null) {
            return;
        }

        const step = (time: number) => {
            if (this.reducedMotionQuery?.matches) {
                this.ambientFrameId = null;
                this.render(time, "motion-settle", false);
                return;
            }
            if (time - this.lastAmbientFrameAt >= AMBIENT_FRAME_MIN_MS) {
                this.lastAmbientFrameAt = time;
                this.render(time, "ambient", true);
            }

            if (
                this.collapseTiles.size > 0 ||
                this.tiles.some(
                    (tile) => tile.kind === "start" || tile.kind === "end" || tile.kind === "portal" || tile.isCurrent
                )
            ) {
                this.ambientFrameId = window.requestAnimationFrame(step);
                return;
            }

            this.ambientFrameId = null;
        };

        this.ambientFrameId = window.requestAnimationFrame(step);
    }

    private syncBounds() {
        const animatedTiles = [...this.collapseTiles.values()].map((entry) => entry.tile);
        // The repeating temple floor supplies distant scenery. Only the
        // lantern's revealed stones need individual canvases and word artwork.
        const current = this.tiles.find(tile => tile.isCurrent);
        // Keep the backing size stable while a move awaits newly revealed words.
        const revealCorners = current ? [
            { x: current.x - MAZE_REVEAL_RADIUS, y: current.y - MAZE_REVEAL_RADIUS },
            { x: current.x + MAZE_REVEAL_RADIUS, y: current.y + MAZE_REVEAL_RADIUS }
        ] : [];
        const combinedTiles = [...this.tiles.filter(tile => tile.revealed), ...animatedTiles, ...revealCorners];

        if (combinedTiles.length === 0) {
            this.bounds = {
                minX: -WORLD_PADDING_PX,
                minY: -WORLD_PADDING_PX,
                width: WORLD_PADDING_PX * 2,
                height: WORLD_PADDING_PX * 2
            };
            this.syncCanvasSize();
            return;
        }

        let minX = Number.POSITIVE_INFINITY;
        let minY = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;

        for (const tile of combinedTiles) {
            const worldX = tile.x * CELL_SIZE;
            const worldY = tile.y * CELL_SIZE;
            minX = Math.min(minX, worldX - TILE_HALF - WORLD_PADDING_PX);
            minY = Math.min(minY, worldY - TILE_HALF - WORLD_PADDING_PX);
            maxX = Math.max(maxX, worldX + TILE_HALF + WORLD_PADDING_PX);
            maxY = Math.max(maxY, worldY + TILE_HALF + WORLD_PADDING_PX);
        }

        this.bounds = {
            minX,
            minY,
            width: Math.max(1, maxX - minX),
            height: Math.max(1, maxY - minY)
        };
        this.syncCanvasSize();
    }

    private isAnimatedTile(tile: MazeBoardRenderTile): boolean {
        return (
            tile.pressed ||
            tile.isCurrent ||
            tile.kind === "start" ||
            tile.kind === "end" ||
            tile.kind === "portal"
        );
    }

    private rebuildStaticLayer(tint: [number, number, number]) {
        if (!this.staticLayerCanvas || !this.staticLayerContext) {
            this.staticLayerDirty = false;
            return;
        }

        const context = this.staticLayerContext;
        const dpr = this.devicePixelRatio || 1;
        let blitCount = 0;
        let rasterCount = 0;
        context.setTransform(dpr, 0, 0, dpr, 0, 0);
        context.clearRect(0, 0, this.bounds.width, this.bounds.height);

        for (const tile of this.tiles) {
            if (!tile.revealed || this.collapseTiles.has(tile.key)) {
                continue;
            }

            const signature = buildTileRenderSignature(tile);
            const cached = this.getTileBitmap(signature);
            const bitmap = cached ?? this.createTileBitmap(tile, tint);

            if (!bitmap) {
                this.drawTile(context, tile, 0, tint, null);
                rasterCount += 1;
                continue;
            }

            if (cached) {
                this.staticTileCacheHitCount += 1;
            } else {
                this.staticTileCacheMissCount += 1;
                this.staticTileRasterCount += 1;
                rasterCount += 1;
                this.storeTileBitmap(signature, bitmap);
            }

            const worldX = tile.x * CELL_SIZE - this.bounds.minX;
            const worldY = tile.y * CELL_SIZE - this.bounds.minY;
            context.save();
            context.globalAlpha *= tile.revealOpacity;
            context.drawImage(
                bitmap.canvas,
                worldX - TILE_HALF - bitmap.padding,
                worldY - TILE_HALF - bitmap.padding,
                bitmap.size,
                bitmap.size
            );
            context.restore();
            this.staticTileBlitCount += 1;
            blitCount += 1;
        }

        this.staticLayerRebuildCount += 1;
        this.lastStaticTileBlitCount = blitCount;
        this.lastStaticTileRasterCount = rasterCount;
        this.staticLayerDirty = false;
    }

    private getTileBitmap(signature: string): TileBitmapCacheEntry | null {
        const cached = this.tileBitmapCache.get(signature);

        if (!cached) {
            return null;
        }

        // Refresh insertion order so the bounded map behaves as a small LRU.
        this.tileBitmapCache.delete(signature);
        this.tileBitmapCache.set(signature, cached);
        return cached;
    }

    private createTileBitmap(
        tile: MazeBoardRenderTile,
        tint: [number, number, number]
    ): TileBitmapCacheEntry | null {
        if (typeof document === "undefined") {
            return null;
        }

        const dpr = this.devicePixelRatio || 1;
        const padding = tile.isCurrent || tile.kind === "start" || tile.kind === "end" || tile.kind === "portal" ? 100 : TILE_BITMAP_PADDING_PX;
        const size = TILE_SIZE + padding * 2;
        const worldX = tile.x * CELL_SIZE - this.bounds.minX;
        const worldY = tile.y * CELL_SIZE - this.bounds.minY;
        const bitmapCenter = padding + TILE_HALF;
        const bodySignature = ["stone", tile.kind, tile.blocked, tile.pressed,
            tile.isCurrent, tile.isReachable, tile.isNextStep].join("\u001f");
        let body = this.getTileBitmap(bodySignature);
        if (!body) {
            const canvas = document.createElement("canvas");
            canvas.width = Math.max(1, Math.round(size * dpr));
            canvas.height = Math.max(1, Math.round(size * dpr));
            const context = canvas.getContext("2d");
            if (!context) return null;
            context.setTransform(dpr, 0, 0, dpr, 0, 0);
            context.translate(bitmapCenter - worldX, bitmapCenter - worldY);
            this.drawTile(context, { ...tile, displayWord: "", displayMark: "", revealOpacity: 1 }, 0, tint, null, false);
            body = { canvas, pixelCount: canvas.width * canvas.height, padding, size };
            this.storeTileBitmap(bodySignature, body);
        }
        if ((!tile.displayWord && !tile.displayMark) || tile.kind === "trail") return body;

        const canvas = document.createElement("canvas");
        canvas.width = body.canvas.width;
        canvas.height = body.canvas.height;
        const context = canvas.getContext("2d");
        if (!context) return null;
        context.setTransform(dpr, 0, 0, dpr, 0, 0);
        context.drawImage(body.canvas, 0, 0, size, size);
        context.translate(bitmapCenter - worldX, bitmapCenter - worldY);
        const tileSize = TILE_SIZE * (tile.pressed ? 0.984 : 1);
        context.globalAlpha = tile.blocked ? 0.9 : 1;
        this.drawTileText(context, tile, worldX - tileSize / 2,
            worldY - tileSize / 2 + (tile.pressed ? 2 : 0), tileSize, this.getTilePalette(tile), tile.kind === "end" ? 0.82 : 1);

        return {
            canvas,
            pixelCount: canvas.width * canvas.height,
            padding,
            size
        };
    }

    private storeTileBitmap(signature: string, entry: TileBitmapCacheEntry) {
        if (entry.pixelCount > MAX_TILE_BITMAP_CACHE_PIXELS) {
            return;
        }

        while (
            this.tileBitmapCache.size > 0 &&
            this.tileBitmapCachePixelCount + entry.pixelCount > MAX_TILE_BITMAP_CACHE_PIXELS
        ) {
            const oldestKey = this.tileBitmapCache.keys().next().value as string | undefined;

            if (!oldestKey) {
                break;
            }

            const oldest = this.tileBitmapCache.get(oldestKey);
            this.tileBitmapCache.delete(oldestKey);
            this.tileBitmapCachePixelCount -= oldest?.pixelCount ?? 0;
        }

        this.tileBitmapCache.set(signature, entry);
        this.tileBitmapCachePixelCount += entry.pixelCount;
    }

    private clearTileBitmapCache() {
        this.tileBitmapCache.clear();
        this.tileBitmapCachePixelCount = 0;
    }

    private syncCanvasSize(): boolean {
        const nextDevicePixelRatio = this.getDevicePixelRatio();
        let changed = false;

        if (Math.abs(this.devicePixelRatio - nextDevicePixelRatio) > 0.001) {
            this.clearTileBitmapCache();
            this.staticLayerDirty = true;
        }

        this.devicePixelRatio = nextDevicePixelRatio;
        const nextLeft = `${this.bounds.minX}px`;
        const nextTop = `${this.bounds.minY}px`;
        const nextWidth = `${this.bounds.width}px`;
        const nextHeight = `${this.bounds.height}px`;

        if (this.canvas.style.left !== nextLeft) {
            this.canvas.style.left = nextLeft;
            changed = true;
        }

        if (this.canvas.style.top !== nextTop) {
            this.canvas.style.top = nextTop;
            changed = true;
        }

        if (this.canvas.style.width !== nextWidth) {
            this.canvas.style.width = nextWidth;
            changed = true;
        }

        if (this.canvas.style.height !== nextHeight) {
            this.canvas.style.height = nextHeight;
            changed = true;
        }

        const width = Math.max(1, Math.round(this.bounds.width * nextDevicePixelRatio));
        const height = Math.max(1, Math.round(this.bounds.height * nextDevicePixelRatio));

        if (this.canvas.width !== width) {
            this.canvas.width = width;
            changed = true;
        }

        if (this.canvas.height !== height) {
            this.canvas.height = height;
            changed = true;
        }

        if (this.staticLayerCanvas) {
            if (this.staticLayerCanvas.width !== width) {
                this.staticLayerCanvas.width = width;
                changed = true;
            }

            if (this.staticLayerCanvas.height !== height) {
                this.staticLayerCanvas.height = height;
                changed = true;
            }
        }

        return changed;
    }

    private getDevicePixelRatio(): number {
        if (typeof window === "undefined") {
            return 1;
        }

        const preferredDpr = clamp(window.devicePixelRatio || 1, 1, MAX_DEVICE_PIXEL_RATIO);
        const cssPixelCount = Math.max(1, this.bounds.width * this.bounds.height);
        const pixelBudgetDpr = Math.sqrt(MAX_CANVAS_BACKING_PIXELS / cssPixelCount);

        return clamp(Math.min(preferredDpr, pixelBudgetDpr), 1, MAX_DEVICE_PIXEL_RATIO);
    }

    private render(time: number, reason: string, ambient: boolean) {
        if (!this.context) {
            return;
        }

        const shouldMeasurePerf = this.onPerfSample !== null;
        const phases: Record<string, number> = {};
        const measure = <T>(phase: string, work: () => T): T => {
            if (!shouldMeasurePerf) {
                return work();
            }

            const startedAt = performance.now();
            const result = work();
            phases[phase] = (phases[phase] ?? 0) + (performance.now() - startedAt);
            return result;
        };
        const renderStartedAt = shouldMeasurePerf ? performance.now() : 0;

        const canvasSizeChanged = measure("syncCanvasSize", () => this.syncCanvasSize());
        if (canvasSizeChanged) {
            this.staticLayerDirty = true;
        }
        const context = this.context;
        const dpr = this.devicePixelRatio || 1;
        const animatedTiles = this.tiles.filter(tile => tile.revealed && (this.isAnimatedTile(tile) || tile.kind === "trail"));
        const partial = ambient && !this.staticLayerDirty && this.collapseTiles.size === 0 && animatedTiles.length > 0;
        const padding = TILE_HALF + 100;
        const accentBounds = animatedTiles.length > 0 ? {
            x: Math.max(0, Math.min(...animatedTiles.map(tile => tile.x * CELL_SIZE - this.bounds.minX)) - padding),
            y: Math.max(0, Math.min(...animatedTiles.map(tile => tile.y * CELL_SIZE - this.bounds.minY)) - padding),
            right: Math.min(this.bounds.width, Math.max(...animatedTiles.map(tile => tile.x * CELL_SIZE - this.bounds.minX)) + padding),
            bottom: Math.min(this.bounds.height, Math.max(...animatedTiles.map(tile => tile.y * CELL_SIZE - this.bounds.minY)) + padding)
        } : { x: 0, y: 0, right: this.bounds.width, bottom: this.bounds.height };
        const redraw = partial ? accentBounds : { x: 0, y: 0, right: this.bounds.width, bottom: this.bounds.height };
        measure("prepareCanvas", () => {
            context.setTransform(dpr, 0, 0, dpr, 0, 0);
            if (!partial || !this.accentContext) context.clearRect(redraw.x, redraw.y, redraw.right - redraw.x, redraw.bottom - redraw.y);
        });
        const tint = this.levelTint;

        measure("drawTiles", () => {
            // Most tiles do not animate every ambient frame, so cache the static board and
            // only repaint the animated accents plus any collapsing wrong tile.
            if (this.staticLayerDirty) {
                this.rebuildStaticLayer(tint);
            }

            if (!this.staticLayerCanvas) {
                for (const tile of this.tiles) {
                    if (!tile.revealed || this.collapseTiles.has(tile.key)) {
                        continue;
                    }

                    this.drawTile(context, tile, this.reducedMotionQuery?.matches ? 0 : time, tint, null);
                }
                return;
            }

            if (this.staticLayerCanvas && (!partial || !this.accentContext)) {
                this.staticLayerPresentCount += 1;
                context.drawImage(this.staticLayerCanvas,
                    redraw.x * dpr, redraw.y * dpr, (redraw.right - redraw.x) * dpr, (redraw.bottom - redraw.y) * dpr,
                    redraw.x, redraw.y, redraw.right - redraw.x, redraw.bottom - redraw.y);
            }

            const accents = this.accentContext ?? context;
            if (this.accentContext) {
                const accentDpr = Math.min(dpr, SITE_TUNING.relicRun.board.maxAccentCanvasDevicePixelRatio);
                const width = accentBounds.right - accentBounds.x;
                const height = accentBounds.bottom - accentBounds.y;
                const pixelWidth = Math.max(1, Math.round(width * accentDpr));
                const pixelHeight = Math.max(1, Math.round(height * accentDpr));
                const style = this.accentCanvas.style;
                for (const [property, value] of Object.entries({ left: `${this.bounds.minX + accentBounds.x}px`, top: `${this.bounds.minY + accentBounds.y}px`, width: `${width}px`, height: `${height}px` })) {
                    if (style.getPropertyValue(property) !== value) style.setProperty(property, value);
                }
                if (this.accentCanvas.width !== pixelWidth) this.accentCanvas.width = pixelWidth;
                if (this.accentCanvas.height !== pixelHeight) this.accentCanvas.height = pixelHeight;
                accents.setTransform(accentDpr, 0, 0, accentDpr, -accentBounds.x * accentDpr, -accentBounds.y * accentDpr);
                accents.clearRect(accentBounds.x, accentBounds.y, width, height);
            }
            for (const tile of this.tiles) {
                if (!tile.revealed || this.collapseTiles.has(tile.key)) {
                    continue;
                }
                if ((this.isAnimatedTile(tile) || tile.kind === "trail") && !this.reducedMotionQuery?.matches) {
                    // Stone panels, words, shadows and glows remain in the static layer.
                    accents.save();
                    accents.globalAlpha *= tile.revealOpacity;
                    const x = tile.x * CELL_SIZE - this.bounds.minX;
                    const y = tile.y * CELL_SIZE - this.bounds.minY + (tile.pressed ? 2 : 0);
                    const size = TILE_SIZE * (tile.pressed ? 0.984 : 1);
                    if (tile.kind === "trail" || tile.isCurrent || ["start", "end", "portal"].includes(tile.kind)) {
                        this.drawSparkles(accents, tile, time, x, y,
                            size * (tile.kind === "portal" ? 0.5 + 20 / size : tile.kind === "start" ? 0.56 : 0.48),
                            tile.kind === "start" ? "rgba(188, 255, 247, 0.9)" : `rgba(${GOLD}, 0.9)`, tile.kind === "end" || tile.kind === "portal" ? 5 : 4);
                    }
                    if (tile.kind !== "trail") this.drawParticleDrift(accents, tile, time, x, y, size, hashString(tile.key));
                    if (tile.kind === "end") this.drawAccentRings(accents, tile, x, y, size, 0.82 + Math.sin(time / 250) * 0.08, this.getTilePalette(tile));
                    accents.restore();
                }
            }
        });

        measure("drawCollapseTiles", () => {
            for (const [key, entry] of [...this.collapseTiles.entries()]) {
                const elapsed = time - entry.startedAt;
                const progress = clamp(elapsed / entry.durationMs, 0, 1);

                if (progress >= 1) {
                    this.collapseTiles.delete(key);
                    continue;
                }

                this.drawTile(context, entry.tile, time, tint, entry);
            }
        });

        if (!shouldMeasurePerf) {
            return;
        }

        const totalMs = performance.now() - renderStartedAt;
        let startTileCount = 0;
        let endTileCount = 0;
        let portalTileCount = 0;
        let currentTileCount = 0;
        let trailTileCount = 0;
        let blockedTileCount = 0;
        let pressedTileCount = 0;

        for (const tile of this.tiles) {
            if (tile.kind === "start") {
                startTileCount += 1;
            }

            if (tile.kind === "end") {
                endTileCount += 1;
            }

            if (tile.kind === "portal") {
                portalTileCount += 1;
            }

            if (tile.kind === "trail") {
                trailTileCount += 1;
            }

            if (tile.isCurrent) {
                currentTileCount += 1;
            }

            if (tile.blocked) {
                blockedTileCount += 1;
            }

            if (tile.pressed) {
                pressedTileCount += 1;
            }
        }

        const sample: MazeCanvasFramePerfSample = {
            ambient,
            blockedTileCount,
            canvasCssHeight: Math.round(this.bounds.height),
            canvasCssWidth: Math.round(this.bounds.width),
            canvasPixelHeight: this.canvas.height,
            canvasPixelWidth: this.canvas.width,
            collapseTileCount: this.collapseTiles.size,
            currentTileCount,
            devicePixelRatio: this.devicePixelRatio,
            endTileCount,
            glowTileCount: startTileCount + endTileCount + portalTileCount + currentTileCount,
            phases,
            portalTileCount,
            pressedTileCount,
            reason,
            recordedAt: performance.now(),
            startTileCount,
            tileCount: this.tiles.length,
            totalMs,
            trailTileCount
        };

        this.lastFramePerfSample = sample;

        if (!ambient) {
            this.lastNonAmbientFramePerfSample = sample;
        }

        if (totalMs >= this.perfThresholdMs) {
            this.onPerfSample?.({
                ...sample,
                phases: { ...sample.phases }
            });
        }
    }

    private drawTile(
        context: CanvasRenderingContext2D,
        tile: MazeBoardRenderTile,
        time: number,
        tint: [number, number, number],
        collapse: CollapseTileAnimation | null,
        animateAccents = true
    ) {
        const worldX = tile.x * CELL_SIZE - this.bounds.minX;
        const worldY = tile.y * CELL_SIZE - this.bounds.minY;
        const palette = this.getTilePalette(tile);
        const pulse = tile.kind === "end" ? 0.82 + Math.sin(time / 250) * 0.08 : 1;
        const scale = collapse ? mix(1, 0.54, easeOut((time - collapse.startedAt) / collapse.durationMs)) : 1;
        const pressedScale = tile.pressed ? 0.984 : 1;
        const offsetProgress = collapse ? easeOut((time - collapse.startedAt) / collapse.durationMs) : 0;
        const translateX = collapse ? collapse.offsetX * offsetProgress : 0;
        const translateY = collapse ? collapse.offsetY * offsetProgress : tile.pressed ? 2 : 0;
        const opacity = collapse ? mix(1, 0, easeOut((time - collapse.startedAt) / collapse.durationMs)) : tile.blocked ? 0.9 : 1;
        const radius = tile.kind === "portal" ? TILE_HALF : TILE_RADIUS;
        const size = TILE_SIZE * scale * pressedScale;
        const half = size / 2;
        const left = worldX - half + translateX;
        const top = worldY - half + translateY;
        const seed = hashString(tile.key);

        context.save();
        context.globalAlpha *= opacity * tile.revealOpacity;

        if (tile.kind === "end" || tile.kind === "portal" || tile.kind === "start" || tile.isCurrent) {
            const glowRadius = tile.kind === "portal" ? size * 1.72 : size * 1.38;
            const glow = context.createRadialGradient(worldX + translateX, worldY + translateY, 0, worldX + translateX, worldY + translateY, glowRadius);
            glow.addColorStop(0, tile.kind === "start" ? "rgba(111, 255, 230, 0.18)" : `rgba(${GOLD}, ${tile.kind === "portal" ? 0.1 : 0.16})`);
            glow.addColorStop(0.65, tile.kind === "start" ? "rgba(111, 255, 230, 0.08)" : `rgba(${GOLD}, 0.06)`);
            glow.addColorStop(1, "rgba(0, 0, 0, 0)");
            context.fillStyle = glow;
            context.beginPath();
            context.arc(worldX + translateX, worldY + translateY, glowRadius, 0, Math.PI * 2);
            context.fill();
        }

        context.shadowBlur = tile.kind === "end" ? 24 : tile.kind === "portal" ? 18 : 14;
        context.shadowOffsetY = tile.kind === "portal" ? 18 : 12;
        context.shadowColor = `rgba(0, 0, 0, ${palette.shadowAlpha})`;

        if (tile.kind === "portal") {
            const portalGradient = context.createRadialGradient(
                worldX + translateX,
                worldY + translateY - size * 0.08,
                size * 0.08,
                worldX + translateX,
                worldY + translateY,
                half
            );
            portalGradient.addColorStop(0, "rgba(255, 255, 255, 0.2)");
            portalGradient.addColorStop(0.18, "rgba(18, 16, 13, 0.12)");
            portalGradient.addColorStop(0.46, "rgba(0, 0, 0, 0.98)");
            portalGradient.addColorStop(1, "rgba(68, 44, 24, 0.98)");
            context.fillStyle = portalGradient;
            context.beginPath();
            context.arc(worldX + translateX, worldY + translateY, half, 0, Math.PI * 2);
            context.fill();
            context.shadowBlur = 0;
            context.lineWidth = 2;
            context.strokeStyle = palette.borderColor;
            context.beginPath();
            context.arc(worldX + translateX, worldY + translateY, half - 1, 0, Math.PI * 2);
            context.stroke();
            if (animateAccents) this.drawSparkles(context, tile, time, worldX + translateX, worldY + translateY, half + 20, palette.glowColor ?? `rgba(${GOLD}, 0.9)`, 5);
        } else {
            const fill = context.createLinearGradient(0, top, 0, top + size);
            fill.addColorStop(0, rgba(palette.gradientTop[0], palette.gradientTop[1], palette.gradientTop[2], 1));
            fill.addColorStop(
                1,
                rgba(
                    mix(palette.gradientBottom[0], tint[0], TILE_TINT_FILL_MIX),
                    mix(palette.gradientBottom[1], tint[1], TILE_TINT_FILL_MIX),
                    mix(palette.gradientBottom[2], tint[2], TILE_TINT_FILL_MIX),
                    1
                )
            );

            roundedRectPath(context, left, top, size, size, radius);
            context.fillStyle = fill;
            context.fill();

            context.shadowBlur = 0;
            context.lineWidth = 2;
            context.strokeStyle = palette.borderColor;
            roundedRectPath(context, left, top, size, size, radius);
            context.stroke();

            const shine = context.createLinearGradient(0, top, 0, top + size);
            shine.addColorStop(0, `rgba(${tint[0]}, ${tint[1]}, ${tint[2]}, ${TILE_TINT_SHINE_ALPHA})`);
            shine.addColorStop(0.52, "rgba(255, 255, 255, 0.02)");
            shine.addColorStop(1, palette.insetColor);
            context.fillStyle = shine;
            roundedRectPath(context, left + 2, top + 2, size - 4, size - 4, Math.max(8, radius - 4));
            context.fill();

            this.drawTileEngravings(context, tile, left, top, size);

            if (tile.kind === "trail") {
                // A permanent carved check distinguishes the traveled route even
                // when motion is reduced or the lantern is fading at its edge.
                context.strokeStyle = "rgba(255, 248, 216, 0.96)";
                context.lineWidth = 4;
                context.lineCap = "round";
                context.lineJoin = "round";
                context.beginPath();
                context.moveTo(worldX + translateX - size * 0.14, worldY + translateY);
                context.lineTo(worldX + translateX - size * 0.025, worldY + translateY + size * 0.115);
                context.lineTo(worldX + translateX + size * 0.18, worldY + translateY - size * 0.13);
                context.stroke();
            }
            if (animateAccents && (tile.kind === "trail" || tile.isCurrent || tile.kind === "start" || tile.kind === "end")) {
                this.drawSparkles(
                    context,
                    tile,
                    time,
                    worldX + translateX,
                    worldY + translateY,
                    size * (tile.kind === "start" ? 0.56 : 0.48),
                    tile.kind === "start" ? "rgba(188, 255, 247, 0.9)" : `rgba(${GOLD}, 0.9)`,
                    tile.kind === "end" ? 5 : 4
                );
            }
        }

        if (tile.kind === "trail") {
            context.restore();
            return;
        }
        context.shadowOffsetX = 0;
        context.shadowOffsetY = 0;
        context.shadowColor = "transparent";
        this.drawTileText(context, tile, left, top, size, palette, pulse);
        this.drawTileBadge(context, tile, left, top, size, palette);
        if (animateAccents || tile.kind !== "end") this.drawAccentRings(context, tile, worldX + translateX, worldY + translateY, size, pulse, palette);
        if (animateAccents) this.drawParticleDrift(context, tile, time, worldX + translateX, worldY + translateY, size, seed);
        context.restore();
    }

    private drawTileEngravings(
        context: CanvasRenderingContext2D,
        tile: MazeBoardRenderTile,
        left: number,
        top: number,
        size: number
    ) {
        if (tile.kind === "trail" || tile.kind === "portal" || size < 48) {
            return;
        }

        const inset = Math.max(7, size * 0.085);
        const leg = Math.max(6, size * 0.09);
        const right = left + size - inset;
        const bottom = top + size - inset;
        const accentAlpha = tile.kind === "end" ? 0.28 : tile.isCurrent || tile.isReachable || tile.isNextStep ? 0.2 : 0.12;

        // Sparse incisions make the board feel like an old relic path without competing with the words.
        context.save();
        context.lineWidth = Math.max(0.85, size * 0.012);
        context.lineCap = "round";
        context.lineJoin = "round";
        context.strokeStyle = `rgba(255, 226, 164, ${accentAlpha})`;
        context.beginPath();
        context.moveTo(left + inset, top + inset + leg);
        context.lineTo(left + inset, top + inset);
        context.lineTo(left + inset + leg, top + inset);
        context.moveTo(right - leg, top + inset);
        context.lineTo(right, top + inset);
        context.lineTo(right, top + inset + leg);
        context.moveTo(right, bottom - leg);
        context.lineTo(right, bottom);
        context.lineTo(right - leg, bottom);
        context.moveTo(left + inset + leg, bottom);
        context.lineTo(left + inset, bottom);
        context.lineTo(left + inset, bottom - leg);
        context.stroke();

        if (tile.kind === "end") {
            const diamondRadius = Math.max(2.5, size * 0.035);
            const diamondY = top + inset + diamondRadius * 0.35;
            context.fillStyle = "rgba(255, 240, 190, 0.34)";
            context.beginPath();
            context.moveTo(left + size / 2, diamondY - diamondRadius);
            context.lineTo(left + size / 2 + diamondRadius, diamondY);
            context.lineTo(left + size / 2, diamondY + diamondRadius);
            context.lineTo(left + size / 2 - diamondRadius, diamondY);
            context.closePath();
            context.fill();
        }

        context.restore();
    }

    private getTilePalette(tile: MazeBoardRenderTile): TilePalette {
        if (tile.kind === "start") {
            return {
                gradientTop: [49, 121, 118],
                gradientBottom: [24, 63, 67],
                borderColor: "rgba(132, 255, 238, 0.76)",
                glowColor: "rgba(79, 255, 228, 0.28)",
                insetColor: "rgba(255, 255, 255, 0.06)",
                wordColor: "#e9fffb",
                markColor: "#c8fff2",
                shadowAlpha: 0.28
            };
        }

        if (tile.kind === "portal") {
            return {
                gradientTop: [28, 20, 14],
                gradientBottom: [8, 8, 8],
                borderColor: tile.isReachable || tile.isNextStep ? "rgba(255, 228, 160, 0.98)" : "rgba(235, 205, 139, 0.9)",
                glowColor: `rgba(${GOLD}, 0.2)`,
                insetColor: "rgba(0, 0, 0, 0.3)",
                wordColor: "#fff6e4",
                markColor: "#ffdca2",
                shadowAlpha: 0.42
            };
        }

        if (tile.kind === "end") {
            return {
                gradientTop: tile.isReachable || tile.isNextStep ? [220, 177, 82] : [214, 173, 84],
                gradientBottom: tile.isReachable || tile.isNextStep ? [133, 84, 21] : [92, 58, 18],
                borderColor: tile.isReachable || tile.isNextStep ? "rgba(255, 240, 183, 0.98)" : "rgba(255, 240, 183, 0.94)",
                glowColor: `rgba(${GOLD}, 0.34)`,
                insetColor: "rgba(255, 247, 223, 0.1)",
                wordColor: "#fff5de",
                markColor: "#fff1c8",
                shadowAlpha: 0.24
            };
        }

        if (tile.kind === "trail") {
            return {
                gradientTop: [213, 165, 68],
                gradientBottom: [128, 84, 25],
                borderColor: "rgba(255, 240, 181, 0.98)",
                glowColor: `rgba(${GOLD}, 0.18)`,
                insetColor: "rgba(255, 247, 223, 0.08)",
                wordColor: "#f5ecd6",
                markColor: "#f5ecd6",
                shadowAlpha: 0.22
            };
        }

        const highlighted = tile.isCurrent || tile.isReachable || tile.isNextStep;
        return {
            gradientTop: tile.isCurrent ? [156, 111, 45] : tile.isNextStep ? [92, 64, 32] : tile.isReachable ? [76, 54, 30] : [72, 49, 28],
            gradientBottom: tile.isCurrent ? [78, 50, 17] : tile.isNextStep ? [43, 29, 15] : tile.isReachable ? [34, 24, 15] : [29, 20, 12],
            borderColor: tile.isCurrent ? "rgba(244, 214, 145, 0.82)" : highlighted ? "rgba(243, 216, 148, 0.82)" : "rgba(133, 99, 54, 0.42)",
            glowColor: tile.isCurrent ? `rgba(${GOLD}, 0.16)` : highlighted ? `rgba(${GOLD}, 0.08)` : null,
            insetColor: tile.isCurrent ? "rgba(255, 236, 188, 0.08)" : "rgba(255, 255, 255, 0.04)",
            wordColor: "#f3ead7",
            markColor: "#e8d8b3",
            shadowAlpha: 0.22
        };
    }

    private drawTileText(
        context: CanvasRenderingContext2D,
        tile: MazeBoardRenderTile,
        left: number,
        top: number,
        size: number,
        palette: TilePalette,
        pulse: number
    ) {
        if (!tile.displayWord) {
            return;
        }

        const maxTextWidth = size - 18;
        let fontSize = tile.kind === "portal" ? 15 : 16;
        context.textAlign = "center";
        context.textBaseline = "middle";
        context.shadowBlur = 0;
        context.shadowOffsetX = 0;
        context.shadowOffsetY = 0;
        context.shadowColor = "transparent";

        while (fontSize > TILE_TEXT_MIN_FONT_SIZE) {
            context.font = `600 ${fontSize}px "Trebuchet MS", "Segoe UI", sans-serif`;

            if (context.measureText(tile.displayWord).width <= maxTextWidth) {
                break;
            }

            fontSize -= 1;
        }

        context.font = `600 ${fontSize}px "Trebuchet MS", "Segoe UI", sans-serif`;
        const displayWord = trimCanvasTextToWidth(context, tile.displayWord, maxTextWidth);
        context.fillStyle = palette.wordColor;
        context.fillText(displayWord, left + size / 2, top + size / (tile.displayMark ? 2.35 : 2.05));

        if (!tile.displayMark) {
            return;
        }

        context.font = `700 ${Math.max(10, fontSize - 4)}px "Trebuchet MS", "Segoe UI", sans-serif`;
        context.fillStyle = palette.markColor;
        const textAlpha = context.globalAlpha;
        context.globalAlpha *= pulse;
        context.fillText(tile.displayMark, left + size / 2, top + size * 0.72);
        context.globalAlpha = textAlpha;
    }

    private drawTileBadge(
        context: CanvasRenderingContext2D,
        tile: MazeBoardRenderTile,
        left: number,
        top: number,
        size: number,
        palette: TilePalette
    ) {
        if (!(tile.isCurrent && tile.kind === "start")) {
            return;
        }

        context.save();
        context.fillStyle = palette.glowColor ?? "rgba(79, 255, 228, 0.24)";
        context.beginPath();
        context.arc(left + size / 2, top + size / 2, size * 0.62, 0, Math.PI * 2);
        context.fill();
        context.restore();
    }

    private drawAccentRings(
        context: CanvasRenderingContext2D,
        tile: MazeBoardRenderTile,
        centerX: number,
        centerY: number,
        size: number,
        pulse: number,
        palette: TilePalette
    ) {
        if (!palette.glowColor) {
            return;
        }

        context.save();
        context.lineWidth = tile.kind === "portal" ? 1.6 : 1.2;
        context.strokeStyle = palette.glowColor;
        context.globalAlpha *= tile.kind === "end" ? 0.74 * pulse : tile.isCurrent ? 0.52 : 0.34;
        context.beginPath();
        context.arc(centerX, centerY, size * (tile.kind === "portal" ? 0.72 : 0.58), 0, Math.PI * 2);
        context.stroke();
        context.restore();
    }

    private drawSparkles(
        context: CanvasRenderingContext2D,
        tile: MazeBoardRenderTile,
        time: number,
        centerX: number,
        centerY: number,
        radius: number,
        color: string,
        count: number
    ) {
        const seed = hashString(`${tile.key}:${tile.kind}`);

        context.save();
        context.fillStyle = color;
        const sparkleAlpha = context.globalAlpha;

        for (let index = 0; index < count; index += 1) {
            const angle = unitFromSeed(seed, index + 1) * Math.PI * 2 + time / 1800;
            const orbit = radius * (0.45 + unitFromSeed(seed, index + 11) * 0.58);
            const sparkleRadius = 1.3 + unitFromSeed(seed, index + 23) * 1.5;
            const pulse = 0.4 + Math.sin(time / 320 + unitFromSeed(seed, index + 37) * Math.PI * 2) * 0.25;
            context.globalAlpha = sparkleAlpha * clamp(pulse, 0.16, 0.82);
            context.beginPath();
            context.arc(centerX + Math.cos(angle) * orbit, centerY + Math.sin(angle) * orbit, sparkleRadius, 0, Math.PI * 2);
            context.fill();
        }

        context.restore();
    }

    private drawParticleDrift(
        context: CanvasRenderingContext2D,
        tile: MazeBoardRenderTile,
        time: number,
        centerX: number,
        centerY: number,
        size: number,
        seed: number
    ) {
        if (!(tile.kind === "trail" || tile.kind === "start" || tile.kind === "end" || tile.isCurrent)) {
            return;
        }

        context.save();
        const color =
            tile.kind === "start"
                ? "rgba(188, 255, 247, 0.84)"
                : tile.kind === "end"
                  ? "rgba(255, 242, 186, 0.84)"
                  : `rgba(${GOLD}, 0.84)`;

        context.fillStyle = color;
        const driftAlpha = context.globalAlpha;
        for (let index = 0; index < 5; index += 1) {
            const phase = ((time / 2200 + unitFromSeed(seed, index + 61)) % 1 + 1) % 1;
            const horizontal = (unitFromSeed(seed, index + 71) - 0.5) * size * 0.56;
            const rise = mix(size * 0.3, -size * 0.56, phase);
            const alpha = (1 - phase) * 0.72;
            context.globalAlpha = driftAlpha * alpha;
            context.beginPath();
            context.arc(centerX + horizontal, centerY + rise, 1.4 + unitFromSeed(seed, index + 81) * 1.2, 0, Math.PI * 2);
            context.fill();
        }

        context.restore();
    }
}
